/**
 * Centralized Brand Tokens
 *
 * Single source of truth for color palette, dark/light neutrals, and typography tokens
 * across Web (MUI/CSS) and Mobile (React Native Paper/Styles).
 */

export const brand = {
  ink: '#15211b',         // forest near-black — dark surfaces, dark text, business anchor
  inkSoft: '#1f2c24',     // dark surface elevation
  paper: '#eef2e8',       // cream — light app background
  paperAlt: '#e6ecdd',    // alt light background band
  card: '#f8fbf2',        // light surface / card
  muted: '#5f6b5f',       // secondary text
  line: '#d3dccb',        // borders / dividers
  accent: '#1d9d63',      // emerald — PRIMARY brand colour, CTAs, primary actions
  accentSoft: '#d7f0e2',  // soft green fills, chips, alert backgrounds
  accentDeep: '#15784c',  // CTA band / hover-deep green
  successDot: '#3ec46d',  // bright success indicator
  warning: '#C8821E',     // warm amber
  error: '#C0392B',       // muted red
  purple: '#7E57C2',      // media/scanner step family
  darkLine: '#2b3a30',
  darkText2: '#b3bdaf',
  darkText3: '#95a092',
};

/**
 * Dark-mode tokens for the web app (additive: `brand` above stays the light/mobile source of truth).
 * Contrast figures are WCAG ratios against the dark surfaces ink (L0) / inkSoft (L1) / raised (L2).
 */
export const brandDark = {
  accent: '#3bbd7c',      // primary/success main in dark: 6.9 on L0, 6.1 on L1, 5.0 on L2
  accentLight: '#5ccb90', // hover for filled buttons (dark goes lighter, not deeper)
  accentDeep: '#2bb071',
  onAccent: '#15211b',    // ink text on every filled colour (6.9 on accent)
  raised: '#2a3a30',      // L2: dialogs, menus, popovers, snackbars
  top: '#34463b',         // L3: tooltips, filled chips, avatar fallbacks
  text2: '#b8c2b4',       // secondary text: 7.9 on L1
  // One step lighter than the spec's #ef8a80 so error text still reads on hovered menus and table rows (L2 + hover).
  error: '#f29b92',
  errorLight: '#f5aea6',
  errorDeep: '#ef8a80',
  warning: '#e8ab4a',
  warningLight: '#f0b85c',
  warningDeep: '#d9942e',
  info: '#4fc3f7',
  infoLight: '#7dd3f7',
  infoDeep: '#29b6f6',
  successText: '#7fe0a8', // success alert text on its tint
  outline: 'rgba(238,242,232,0.45)', // input/chip/toggle/outlined-button edges: 3.8 on L1
  line: 'rgba(238,242,232,0.16)',    // dividers, card and table borders
  scrollThumb: '#5a6b60',
  bodyText: '#dfe5dc',    // long-form article body on ink (a touch softer than paper)
  codeBg: '#0e1813',      // code blocks on ink
};

export const typography = {
  headingFont: '"DM Sans", "Helvetica Neue", "Arial", sans-serif',
  bodyFont: '"DM Sans", "Helvetica Neue", "Arial", sans-serif',
};
